import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { cn } from 'cn';
import { Bookmark } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchAvailableLanguages, fetchKhutbahReading, ApiError } from './api';
import { fetchLearning } from './learningApi';
import { DocumentCard } from '@/features/card/DocumentCard';
import { ReportForm } from './ReportForm';
import { REPORT_STATUS_TEXT, deviceKey } from './reporting';
import { PassageBlock, SectionLabel } from './PassageBlock';
import { TrustStrip } from '@/components/trust-strip';
import { ReaderControls } from './ReaderControls';
import {
  isLeading,
  isReadingWidth,
  READING_WIDTH_CAPS,
  isStep,
  readerStyle,
  type Leading,
  type ReaderStep,
  type ReadingWidth,
} from './readerSettings';
import {
  hasWords,
  isRenderable,
  readingQueryKey,
  type KhutbahReading,
  type PassageReading,
} from './types';
import { useUrlParam } from './useUrlState';
import { useStoredPreference } from './useStoredPreference';
import { recordRead, toggleSaved } from '@/features/library/libraryStore';
import { useIsSaved } from '@/features/library/useLibrary';
import { effectiveMode, isViewMode, type ViewMode } from './viewMode';
import { IconRail } from './IconRail';
import { ChatPanel } from '@/features/chat/ChatPanel';
import { SectionsRail } from './SectionsRail';
import { quotedGroups, railSections } from './outline';
import { OutlineSheet } from './OutlineSheet';
import { useAssistantLanguage } from '@/features/settings/useAssistantLanguage';

/**
 * The reading surface: a khutbah's Arabic beside its translation.
 *
 * The product's core screen — everything else exists to bring a reader here.
 */

/** Native names, so a language is offered in its own script. */
const LANGUAGE_NAMES: Record<string, string> = {
  ar: 'العربية',
  en: 'English',
  ml: 'മലയാളം',
  kn: 'ಕನ್ನಡ',
  ur: 'اردو',
  ta: 'தமிழ்',
};

function formatFriday(isoDate: string): string {
  // The intended Friday, not the publication date — brief §5 keeps those apart
  // and the reader cares about the former.
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Groups passages into their sections, preserving order.
 *
 * Derived during render rather than stored in state: it is a pure function of
 * the passages, and mirroring it into state with an effect is the mistake the
 * React rules call out.
 */
function groupBySection(
  passages: readonly PassageReading[],
): { sectionType: string; pageNo: number | null; passages: PassageReading[] }[] {
  const groups: {
    sectionType: string;
    pageNo: number | null;
    passages: PassageReading[];
  }[] = [];
  for (const passage of passages) {
    const last = groups.at(-1);
    if (last && last.sectionType === passage.sectionType) {
      last.passages.push(passage);
    } else {
      groups.push({
        sectionType: passage.sectionType,
        pageNo: passage.pageNo,
        passages: [passage],
      });
    }
  }
  return groups;
}

function ReadingSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy>
      <Skeleton className="h-6 w-56" />
      <Skeleton className="h-4 w-32" />
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-12 w-11/12" />
        </div>
      ))}
    </div>
  );
}

function ReadingError({ error }: { error: unknown }) {
  const notFound = error instanceof ApiError && error.notFound;
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="text-base font-semibold text-card-foreground">
        {notFound ? 'This khutbah is not available' : 'Could not load the khutbah'}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {notFound
          ? // The server does not distinguish "no such khutbah" from "not
            // published", so neither does this message.
            'It may not have been published yet, or the link may be wrong.'
          : error instanceof Error
            ? error.message
            : 'Something went wrong reaching the server.'}
      </p>
    </div>
  );
}

function ReadingHeader({
  reading,
  condensed,
  saved,
  onToggleSaved,
}: {
  reading: KhutbahReading;
  /** Whether this khutbah is in the reader's library on this device. */
  saved: boolean;
  onToggleSaved: () => void;
  /**
   * Collapsed to a single line, which is what the reader sees once they have
   * scrolled into the khutbah.
   *
   * The mock's reader screen asks for exactly this: "On scroll the title
   * collapses to a sticky bar that keeps both badges." The chrome is fixed
   * here, so without it the full header -- Arabic title, translated title,
   * date line and trust strip -- held 274px of a 390x844 phone for the whole
   * read, 32% of the display, long after a reader knew which khutbah they
   * were in. What they still need at that point is the two badges, because
   * those qualify every line they are reading; the titles they can get back
   * by scrolling to the top.
   */
  condensed: boolean;
}) {
  // Both sides of the ratio must describe the same set, or the numerator can
  // exceed the denominator -- which it did: "24 of 23 passages". `translated`
  // counted every passage with a translation while `translatable` excluded
  // Qur'an passages, several of which are translated.
  const translatable = reading.passages.filter(hasWords);
  const translated = translatable.filter((p) => p.translation !== null).length;

  // The title in the reader's own language.
  //
  // The khutbah's topic is the first thing a reader needs and the one thing
  // the header showed only in Arabic. `titleOriginal` is taken from a passage
  // at extraction, so that passage's translation IS the translated title --
  // no new field, and it inherits the same provenance as everything else.
  const titleTranslated = reading.titleOriginal
    ? (reading.passages.find(
        (p) => p.sourceText.trim() === reading.titleOriginal?.trim(),
      )?.translation?.text ?? null)
    : null;

  // One translation, so one trust claim. Any passage that has one carries
  // the same origin and review state — they are entered in a single act —
  // so the first is representative, and the strip says so once.
  const translation = reading.passages.find((p) => p.translation !== null)?.translation
    ?? null;

  const save = (
    <button
      type="button"
      aria-pressed={saved}
      onClick={onToggleSaved}
      title={saved ? 'Remove from my library' : 'Keep this in my library'}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs',
        saved
          ? 'border-primary bg-secondary text-secondary-foreground'
          : 'border-input text-muted-foreground hover:text-foreground',
      )}
    >
      <Bookmark
        className={cn('size-3.5', saved && 'fill-current')}
        aria-hidden="true"
      />
      {saved ? 'Saved' : 'Save'}
    </button>
  );

  const trust = (
    <TrustStrip
      institutionKey={reading.institutionKey}
      sourceKind={reading.institutionKey ? 'publisher' : 'user'}
      institutionName={reading.institutionName}
      fetchedOn={null}
      translation={translation}
      stale={false}
      languageName={reading.languageName}
    />
  );

  if (condensed) {
    return (
      // One line: the khutbah's own title, small and truncated, and the two
      // badges it qualifies. The badges are never what gets dropped.
      <header className="flex min-w-0 items-center gap-3">
        {reading.titleOriginal ? (
          <h1
            lang={reading.sourceLanguageCode}
            dir={reading.sourceDirection}
            className={cn(
              reading.sourceScriptClass,
              'min-w-0 flex-1 truncate text-base font-normal text-reading-foreground',
            )}
          >
            {reading.titleOriginal}
          </h1>
        ) : (
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold">
            Khutbah of {formatFriday(reading.intendedDate)}
          </h1>
        )}
        <div className="flex shrink-0 items-center gap-2">
          {trust}
          {save}
        </div>
      </header>
    );
  }

  return (
    <header className="flex flex-col gap-1.5 sm:gap-2.5">
      {/* The khutbah's own title, in its own script — not "Friday khutbah".
          A reader knows which khutbah this is by its title or not at all. */}
      {reading.titleOriginal ? (
        <h1
          lang={reading.sourceLanguageCode}
          dir={reading.sourceDirection}
          className={cn(
            reading.sourceScriptClass,
            'text-xl font-normal text-reading-foreground sm:text-2xl',
          )}
        >
          {reading.titleOriginal}
        </h1>
      ) : (
        <h1 className="text-2xl font-semibold tracking-tight">
          Khutbah of {formatFriday(reading.intendedDate)}
        </h1>
      )}

      {/* The topic in the reading language, directly under the Arabic. Not a
          heading of its own -- it is the same title, said twice. */}
      {titleTranslated ? (
        <p
          lang={reading.languageCode}
          dir={reading.direction}
          className={cn(
            reading.scriptClass,
            'text-base text-pretty text-reading-foreground sm:text-lg',
          )}
        >
          {titleTranslated}
        </p>
      ) : null}

      <p className="text-sm text-muted-foreground">
        {formatFriday(reading.intendedDate)}
        {reading.institutionName ? ` · ${reading.institutionName}` : ''}
        {translatable.length > 0 ? (
          <>
            {' · '}
            {translated} of {translatable.length} passages in {reading.languageName}
          </>
        ) : null}
      </p>

      {/* Once, here — not on every passage. Save sits with them because all
          three are statements about this khutbah rather than reading
          settings, which live in the toolbar below. */}
      <div className="flex flex-wrap items-center gap-2">
        {trust}
        {save}
      </div>
    </header>
  );
}

/**
 * Whether there is room for two columns.
 *
 * Subscribed to as an external store rather than mirrored into state by an
 * effect — the media query IS the source of truth, and copying it into state
 * is the pattern the React rules name outright.
 */
function useWideViewport(minWidth: number): boolean {
  const query = `(min-width: ${minWidth}px)`;
  return useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => true,
  );
}

/**
 * Where two columns actually fit, which depends on the chat.
 *
 * The brand book gives both numbers: "The reader shows Arabic and translation
 * side by side from 1280px with the chat open, or from 1024px with it closed;
 * otherwise stacked."
 *
 * It was a flat 768px, which ignored the two columns the shell spends before
 * the reader gets any -- the icon rail and the Sections rail -- and the 340px
 * the chat takes after it. At 1600px with the chat open that left two columns
 * of about 460px each, so asking a question made the Arabic and its
 * translation harder to read, which is the opposite of what the chat is for.
 */
const SIDE_BY_SIDE_MIN = { withChat: 1280, withoutChat: 1024 } as const;

/**
 * Which section the reader is currently looking at.
 *
 * An Effect is right here, unlike most places: an IntersectionObserver IS an
 * external system, and subscribing to one is exactly what Effects are for.
 * The alternative — recomputing scroll position during render — would be the
 * actual mistake.
 */
function useActiveSection(passageIds: readonly string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  // Held in a ref so the observer is not torn down and rebuilt on every
  // render, which would make it miss the scrolling it exists to watch.
  const visible = useRef(new Set<string>());

  useEffect(() => {
    visible.current.clear();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id.replace('passage-', '');
          if (entry.isIntersecting) {
            visible.current.add(id);
          } else {
            visible.current.delete(id);
          }
        }
        // The topmost visible passage in document order, so the rail follows
        // reading rather than jumping to whichever entry fired last.
        const first = passageIds.find((id) => visible.current.has(id));
        setActiveId(first ?? null);
      },
      // A band across the upper middle: a passage counts as "where you are"
      // once it reaches reading height, not when it first peeks in.
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );

    for (const id of passageIds) {
      const element = document.getElementById(`passage-${id}`);
      if (element) {
        observer.observe(element);
      }
    }
    return () => observer.disconnect();
  }, [passageIds]);

  return activeId;
}

export function ReaderScreen({ khutbahId }: { khutbahId: string }) {
  // The URL wins, because a shared link must open in the language it names.
  // Absent that, the reader's own default from Settings — which is what makes
  // that setting more than a label.
  const [defaultLanguage] = useStoredPreference(
    'mimbar.reader.language',
    'ml',
    (v): v is string => typeof v === 'string' && /^[a-z]{2,3}$/.test(v),
  );
  const [language, setLanguage] = useUrlParam('language', defaultLanguage);
  // Layout and type settings persist: a reader who enlarged the text once
  // should not have to do it again every Friday.
  const [storedStep, setStoredStep] = useStoredPreference('mimbar.reader.step', '3', isStep);
  const [leading, setLeading] = useStoredPreference<Leading>(
    'mimbar.reader.leading', 'normal', isLeading);
  const [view, setView] = useStoredPreference<ViewMode>(
    'mimbar.reader.view', 'side-by-side', isViewMode);
  // `normal` by default: the same cap Home and Library use, so arriving at a
  // khutbah from a list does not change the page's width under the reader.
  // The language on screen, not the stored default: this reader may have
  // arrived on a link that names one.
  const assistantLanguage = useAssistantLanguage(language);
  const [readingWidth, setReadingWidth] = useStoredPreference<ReadingWidth>(
    'mimbar.reader.width', 'normal', isReadingWidth);
  const [quotedFilter, setQuotedFilter] = useState<'quran' | 'hadith' | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [reporting, setReporting] = useState<number | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  // Whether the reader has scrolled far enough into the khutbah that the
  // header should give its space back. Hysteresis, not one threshold: a
  // single boundary makes the header flicker open and shut when a reader
  // rests exactly on it, and the header changing height moves the content
  // under them, which re-crosses the boundary.
  const [condensed, setCondensed] = useState(false);
  const saved = useIsSaved(khutbahId);

  // What this device already reported, so a reader sees the outcome without
  // ever signing in.
  const myReports = useQuery({
    queryKey: ['khutbah', khutbahId, 'my-reports'],
    queryFn: async ({ signal }) => {
      const key = deviceKey();
      if (key === '') return [] as { passageOrdinal: number; status: string }[];
      const response = await fetch(
        `/api/public/khutbah/${encodeURIComponent(khutbahId)}/my-reports`,
        {
          signal,
          // In a header, not the query string. The key is bearer-shaped —
          // whoever holds it can read what this device reported — and a URL
          // reaches access logs, Referer headers and browser history.
          headers: { Accept: 'application/json', 'X-Device-Key': key },
        },
      );
      if (!response.ok) return [];
      const body: unknown = await response.json();
      return Array.isArray(body)
        ? body.flatMap((entry) => {
            const row = entry as Record<string, unknown>;
            return typeof row.passageOrdinal === 'number' &&
              typeof row.status === 'string'
              ? [{ passageOrdinal: row.passageOrdinal, status: row.status }]
              : [];
          })
        : [];
    },
    retry: false,
  });
  const [wordMeaning, setWordMeaning] = useStoredPreference<'on' | 'off'>(
    'mimbar.reader.wordMeaning',
    'off',
    (value): value is 'on' | 'off' => value === 'on' || value === 'off',
  );
  const [outlineOpen, setOutlineOpen] = useState(false);

  // The chat is docked from lg, so when it is open it really does take a
  // column out of the shell -- the threshold has to know that.
  const wide = useWideViewport(
    chatOpen ? SIDE_BY_SIDE_MIN.withChat : SIDE_BY_SIDE_MIN.withoutChat,
  );

  const reading = useQuery({
    queryKey: readingQueryKey(khutbahId, language),
    queryFn: ({ signal }) => fetchKhutbahReading(khutbahId, language, signal),
    // A 404 means not published. Retrying cannot change that, and retrying
    // twice just delays the message.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.notFound) && failureCount < 2,
  });

  // The khutbah's glosses, per language. Fetched whether or not the layer is
  // on, because the toolbar has to know whether there is anything to offer —
  // a toggle that turns on and shows nothing is worse than a disabled one.
  const learning = useQuery({
    queryKey: ['khutbah', khutbahId, 'learning', language],
    queryFn: ({ signal }) => fetchLearning(khutbahId, language, signal),
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  const languages = useQuery({
    queryKey: ['khutbah', khutbahId, 'languages'],
    queryFn: ({ signal }) => fetchAvailableLanguages(khutbahId, signal),
    retry: false,
  });

  const passages = reading.data?.passages ?? EMPTY_PASSAGES;
  // Memoised by identity rather than recomputed: the observer effect depends
  // on this array, and a fresh array each render would tear the observer down
  // and rebuild it on every scroll frame.
  const passageIds = useMemo(() => passages.map((p) => p.passageId), [passages]);
  // Hooks run before any early return, which is why the data is read through
  // optional access above rather than after the loading branches below.
  const activePassageId = useActiveSection(passageIds);

  // Record the read once the khutbah has actually arrived.
  //
  // A genuine external system — this writes to `localStorage`, which the
  // library reads in another screen and another tab, so it is not state
  // being mirrored. Keyed on the id alone: re-reading the same khutbah in a
  // second language is the same reading, and the entry moves rather than
  // duplicating.
  const loaded = reading.isSuccess;
  useEffect(() => {
    if (loaded) {
      recordRead(khutbahId, new Date());
    }
  }, [loaded, khutbahId]);

  // A citation link arrives as `#passage-<id>`, and the browser's own anchor
  // jump has already failed by the time the passages exist — it runs on load,
  // and the khutbah is fetched after. So the jump happens once the content is
  // on screen.
  //
  // It has to happen more than once. Measured: a single jump on load landed
  // 1561px short of its passage, because the page keeps growing underneath
  // it — the five scripts' fonts swap in after first paint, and the word
  // meanings arrive in a second request and add a chip row to most passages.
  // Both push the target down after the scroll has already been aimed.
  //
  // So it re-aims on each of those settling, and not on a timer: the hash,
  // the fonts and the scroll position are all external systems, and
  // `document.fonts.ready` and the query's own state say when they are done
  // far more reliably than any delay would.
  //
  // The ref keys on the hash AND what has settled, so each stage re-aims once
  // and a reader who scrolls away afterwards is never yanked back.
  //
  // Two forms arrive. `#passage-<id>` comes from a copied citation, where the
  // id is known. `?passage=<n>` comes from the standalone chat, which knows
  // only the number a citation showed — it has the answer's ordinals, not the
  // khutbah's ids — so the number is resolved to an id here, where the
  // passages are.
  const jumped = useRef<string | null>(null);
  const meaningsSettled = !learning.isPending;
  useEffect(() => {
    if (!loaded) return;

    const hash = window.location.hash;
    const byOrdinal = new URLSearchParams(window.location.search).get('passage');
    let targetId: string | null = null;

    if (hash.startsWith('#passage-')) {
      targetId = hash.slice(1);
    } else if (byOrdinal !== null) {
      const ordinal = Number(byOrdinal);
      // Citations are one-based for a reader; passage ordinals are not.
      const passage = Number.isInteger(ordinal)
        ? reading.data?.passages.find((p) => p.ordinal + 1 === ordinal)
        : undefined;
      targetId = passage ? `passage-${passage.passageId}` : null;
    }
    if (targetId === null) return;

    const stage = `${targetId}|${String(meaningsSettled)}`;
    if (jumped.current === stage) return;
    jumped.current = stage;

    const aim = () =>
      document
        .getElementById(targetId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // After paint: the element is rendered in the same commit that set
    // `loaded`, so it is not in the document when this effect runs.
    requestAnimationFrame(() => {
      aim();
      void document.fonts?.ready.then(aim);
    });
  }, [loaded, meaningsSettled, reading.data]);

  if (reading.isPending) {
    return <ReadingSkeleton />;
  }
  if (reading.isError) {
    return <ReadingError error={reading.error} />;
  }

  const data = reading.data;
  const step = Number(storedStep) as ReaderStep;
  const setStep = (next: ReaderStep) => setStoredStep(String(next) as `${ReaderStep}`);
  // Side by side on a phone falls back to stacked rather than being disabled,
  // so the chosen setting survives the device.
  const activeView = effectiveMode(view, wide);

  const offered =
    languages.data && languages.data.length > 0 ? languages.data : [data.languageCode];

  const sections = railSections(data.passages);
  const quoted = quotedGroups(data.passages);
  const activeSectionType =
    data.passages.find((p) => p.passageId === activePassageId)?.sectionType ?? null;

  // Artifacts out, evidence in. See `isRenderable`: a passage flagged
  // `sourceUncertain` is kept even with no words and no translation, because
  // that flag IS the thing a reader needs to see.
  const readable = data.passages.filter(isRenderable);
  const shown = quotedFilter
    ? readable.filter((p) => p.passageType === quotedFilter)
    : readable;

  const jumpTo = (passageId: string) => {
    // Clearing the filter first: jumping to a section while the reader shows
    // only quotations would scroll to something that is not rendered.
    setQuotedFilter(null);
    setOutlineOpen(false);
    requestAnimationFrame(() => {
      document
        .getElementById(`passage-${passageId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const outline = (
    <SectionsRail
      sections={sections}
      quoted={quoted}
      activeSectionType={activeSectionType}
      onJump={jumpTo}
      quotedFilter={quotedFilter}
      onQuotedFilter={(kind) => {
        setQuotedFilter(kind);
        setOutlineOpen(false);
      }}
    />
  );

  // How wide the khutbah is allowed to be.
  //
  // `full` is the default: the text fills the central pane it has been
  // allocated, the way quran.com does. `measure` narrows it to
  // `--reader-measure` (40rem) and centres it. See READING_WIDTHS in
  // readerSettings for why the brand book's cap became a setting.
  //
  // ONE frame width, never one per view. It used to be two measures for side
  // by side and one for everything else, which had two consequences, both
  // reported:
  //
  //  - Choosing "Top and bottom" collapsed the whole frame from 1312px to
  //    640px at a 1600px viewport -- the header, the toolbar and the reading
  //    surface all shrank together, so changing how the PANES stack changed
  //    the width of the chrome above them.
  //  - It keyed off `view`, the stored preference, while the passages render
  //    with `activeView`, the effective one. On a 900px tablet with side by
  //    side chosen, the frame reserved two measures and the content drew one
  //    column, so the two disagreed about what was on screen.
  // Side by side needs two measures to put two columns of prose up; every
  // other view is one column and takes one. At `full` there is no cap at all
  // and the pane decides.
  //
  // Doubling applies to `measure` only. `normal` is already wide enough for
  // two columns of prose inside it, and doubling an 80rem cap to 160rem would
  // be indistinguishable from `full` on every display anyone reads this on.
  // Whether the outline is a permanent column. `full` means the display, so
  // the rail stands down there and the toolbar's Sections button takes over.
  // Computed once and passed to the toolbar, so the rail and the button that
  // replaces it cannot disagree about which is showing.
  const railShown = readingWidth !== 'full';

  const cap = READING_WIDTH_CAPS[readingWidth];
  const contentWidth =
    cap === null
      ? undefined
      : {
          maxWidth:
            readingWidth === 'measure' && activeView === 'side-by-side'
              ? 'calc(var(--reader-measure) * 2 + 2rem)'
              : cap,
        };

  return (
    // The shell owns the viewport and does not scroll; the reading column does.
    //
    // It was one tall document -- 12,904px for this khutbah -- so the rail,
    // outline, toolbar and chat all scrolled away with the text. A reader
    // partway through a khutbah could not reach the language picker or the
    // outline without scrolling back to the top, which was reported as the
    // scrolling being wrong, and it was.
    <div className="flex h-[calc(100dvh-var(--app-bar-height))] overflow-hidden">
      <IconRail />
      {/*
        At `full` the outline rail gives its 200px back.

        "Full width" has to mean the browser, and it did not: the rail sits
        between the icon rail and the reader, so the pane the khutbah fills
        was already 256px short of the display. Measured at 1440px, `normal`
        and `full` both produced a 1152px column — identical, because the
        80rem cap is wider than a 1152px pane and so never applied. The
        setting looked broken, and from the reader's side it was.

        The outline is not lost, only moved: the toolbar's Sections button
        appears whenever the rail does not, which is the same disclosure
        every width below `lg` already uses.
      */}
      {railShown ? outline : null}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Fixed chrome: part of the shell now, not the scrolling content. */}
        <div className="shrink-0 border-b border-border bg-background">
          {/*
            Tighter on a small screen. Measured at 390x844 this chrome took
            398px -- 47% of the display -- leaving 446px for the khutbah. The
            header and toolbar are the same information either way; they just
            stop claiming half a phone for it.
          */}
          <div
            className="me-auto flex w-full flex-col gap-2 px-4 py-2 sm:gap-3 sm:py-3"
            style={contentWidth}
          >
            <ReadingHeader
              reading={data}
              condensed={condensed}
              saved={saved}
              onToggleSaved={() => toggleSaved(khutbahId)}
            />
            <ReaderControls
              languages={offered}
              languageNames={LANGUAGE_NAMES}
              language={language}
              onLanguageChange={setLanguage}
              step={step}
              onStepChange={setStep}
              leading={leading}
              onLeadingChange={setLeading}
              view={view}
              onViewChange={setView}
              readingWidth={readingWidth}
              onReadingWidthChange={setReadingWidth}
              wordMeaning={wordMeaning === 'on'}
              onWordMeaningChange={(on) => setWordMeaning(on ? 'on' : 'off')}
              glossedWords={learning.data ? learning.data.glossed : null}
              onOpenOutline={() => setOutlineOpen(true)}
              outlineInRail={railShown}
              chatOpen={chatOpen}
              onToggleChat={() => setChatOpen((was) => !was)}
            />
          </div>
        </div>

        {/*
          The only scroll container. Full width, so the shell uses the display;
          any cap on the text is applied inside, by the width setting.
          `min-h-0` is what lets a flex child actually scroll instead of
          growing its parent.
        */}
        <main
          className="flex min-h-0 flex-col flex-1 overflow-y-auto px-4 pb-20"
          onScroll={(event) => {
            const top = event.currentTarget.scrollTop;
            setCondensed((was) => (was ? top > 32 : top > 96));
          }}
        >
          {/*
            Start-aligned, not centred, and only here.

            A capped column centred in a wide pane puts the khutbah in the
            middle of the display with equal margins either side; against the
            icon rail and the outline that reads as the text having drifted
            away from its own navigation. Aligned to the start it sits where
            the rail left off and the eye returns to one edge every line.

            `me-auto` rather than `mr-auto`: in an RTL interface the start
            edge is the right one, and the Arabic has to begin at the same
            edge its script does.

            The chrome above uses the same rule, or the title and toolbar
            would sit centred over a text that is not.
          */}
          <div className="me-auto w-full" style={contentWidth}>
          {showCard ? (
            <section className="mt-4 rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold">This khutbah in one screen</h2>
                <button
                  type="button"
                  onClick={() => setShowCard(false)}
                  className="text-sm text-primary underline"
                >
                  Close
                </button>
              </div>
              <DocumentCard
                khutbahId={khutbahId}
                language={language}
                sourceScript={data.sourceScriptClass}
              />
            </section>
          ) : (
            <button
              type="button"
              onClick={() => setShowCard(true)}
              className="mt-4 text-sm text-primary underline"
            >
              Show the khutbah in one screen
            </button>
          )}

          {quotedFilter ? (
            <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              Showing only the {quotedFilter === 'quran' ? 'Qur’an verses' : 'hadith'} in
              this khutbah.{' '}
              <button
                type="button"
                onClick={() => setQuotedFilter(null)}
                className="text-primary underline"
              >
                Show the whole khutbah
              </button>
            </p>
          ) : null}

          {/*
            `reader` is the class globals.css hangs the type scale off: it sets
            font-size from --reader-step and adds the per-script leading on
            top. Without it the size and line-height controls do nothing.
          */}
          <div
            className="reader mt-4 rounded-lg bg-reading-surface px-5 py-2 md:px-10"
            style={readerStyle(step, leading)}
          >
            {shown.length === 0 ? (
              <p className="py-10 text-sm text-muted-foreground">
                This khutbah has no passages yet. It may still be processing.
              </p>
            ) : (
              groupBySection(shown).map((group, index) => (
                <section key={`${group.sectionType}-${index}`}>
                  <SectionLabel sectionType={group.sectionType} pageNo={group.pageNo} />
                  {group.passages.map((passage) => (
                    <PassageBlock
                      // The passage id, never the array index: passages reorder
                      // when a new source version is ingested.
                      key={passage.passageId}
                      passage={passage}
                      scriptClass={data.scriptClass}
                      direction={data.direction}
                      languageCode={data.languageCode}
                      sourceLang={data.sourceLanguageCode}
                      sourceDir={data.sourceDirection}
                      sourceScript={data.sourceScriptClass}
                      words={
                        wordMeaning === 'on'
                          ? (learning.data?.byPassage.get(passage.passageId) ?? [])
                          : []
                      }
                      onReport={() => setReporting(passage.ordinal)}
                      reportStatus={
                        REPORT_STATUS_TEXT[
                          (myReports.data ?? []).find(
                            (r) => r.passageOrdinal === passage.ordinal,
                          )?.status ?? ''
                        ]
                      }
                      view={activeView}
                    />
                  ))}
                </section>
              ))
            )}
          </div>

          {reading.isFetching ? (
            <p className="mt-3 text-xs text-muted-foreground" role="status">
              Refreshing…
            </p>
          ) : null}

          </div>
        </main>
      </div>

      {/* The dock's placeholder is replaced by the real panel. It still
          renders the honest "not answering yet" state when no provider is
          configured — which is every environment today — but it is now the
          same component that will answer once one is. */}
      {reporting !== null ? (
        <ReportForm
          khutbahId={khutbahId}
          passageOrdinal={reporting}
          language={language}
          onClose={() => setReporting(null)}
          onFiled={() => void myReports.refetch()}
        />
      ) : null}

      {/*
        The chat DOCKS at lg and above, and overlays below it.

        This reverses the float that reversed ADR 0005 -- see the ADR's second
        2026-10-24 addendum. Both reversals are the product owner's call, and
        the second one is only cheap because of what changed in between.

        The float existed because a docked 340px panel had to take its width
        from something, and with the khutbah filling its pane the only thing
        left to take it from was the khutbah: asking a question narrowed the
        text and reflowed the page mid-sentence. The reading width now
        defaults to `normal` (80rem) rather than `full`, so on any display
        wide enough to dock there is slack between the cap and the pane for
        the panel to take. The text only narrows once the pane drops below
        the cap -- and at that point the reader asked for the trade by opening
        the chat, rather than having it sprung on them.

        Below lg there is no slack to take and docking would halve the
        khutbah, so it stays an overlay there: fixed, dismissible, with a
        shadow rather than flush. No scrim and no focus trap in that state --
        the reader must stay readable and scrollable underneath, so it is a
        non-modal overlay.
      */}
      {chatOpen ? (
        <aside
          aria-label="Ask about this khutbah"
          className={cn(
            'z-40 flex flex-col overflow-hidden border-s border-border bg-background',
            'fixed inset-y-0 end-0 w-full max-w-[380px] shadow-2',
            // Docked: a flex child of the shell row, so it takes its width
            // from the row and the reading column reflows around it rather
            // than being covered. `static` makes the inset utilities above
            // inert without having to unset each one.
            'lg:static lg:w-[340px] lg:max-w-none lg:shrink-0 lg:shadow-none',
          )}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
            {/* The panel's one heading. ChatPanel used to carry a second one
                directly beneath this, so the word appeared twice. */}
            <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Ask
            </h2>
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              aria-label="Close the chat"
              className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted"
            >
              Close
            </button>
          </div>
          {/* The chat scrolls on its own, independently of the khutbah. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ChatPanel
              khutbahId={khutbahId}
              // The assistant's language, which is the reading language only
              // while the reader has not said otherwise. Both chat entry
              // points resolve it the same way, so the docked panel and the
              // standalone screen cannot answer in different languages.
              language={assistantLanguage}
              onCitation={(ordinal) => {
                const passage = data.passages[ordinal - 1];
                if (passage) {
                  document
                    .getElementById(`passage-${passage.passageId}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
            />
          </div>
        </aside>
      ) : null}

      {/* Below lg the outline is a sheet rather than absent: the spec makes it
          the Sections sheet from the bottom bar, and a rail that simply
          vanishes on a phone takes the khutbah's shape with it. */}
      {/*
        `railShownAtLg` is the same flag the rail and the Sections button use.
        Without it the sheet keeps its `lg:hidden` and full width leaves the
        outline unreachable on a desktop.
      */}
      <OutlineSheet
        open={outlineOpen}
        onClose={() => setOutlineOpen(false)}
        railShownAtLg={railShown}
      >
        {outline}
      </OutlineSheet>
    </div>
  );
}

/** Empty but stable, so the observer effect does not rebuild every render. */
const EMPTY_PASSAGES: readonly PassageReading[] = [];
